import executeGetAllSchedulesRequest from "api/common/schedules.api";
import { ScheduleStatus, ScheduleType } from "enums/schedule.enum";
import { IGetAllSchedulesResponse, IScheduleRowViewModel } from "types/schedule";
import toScheduleRowViewModel from "utils/schedule-mapper";
import {
    IGetAllSchedulesOptions,
    ISchedulesPage,
    ISchedulesQuery,
    ISchedulesRepository,
    SCHEDULES_DRAIN_PAGE_SIZE,
} from "./schedules.repository";

const EMPTY_PAGE: ISchedulesPage = { rows: [], total: 0, nextStartKey: null };

export const SchedulesRepository: ISchedulesRepository = {
    async getSchedules(query: ISchedulesQuery): Promise<ISchedulesPage> {
        const response = await executeGetAllSchedulesRequest({
            pageSize: query.pageSize,
            status: query.status as ScheduleStatus | undefined,
            type: query.type as ScheduleType | undefined,
            startKey: query.startKey,
            total: query.total,
        });

        if (!response || !Array.isArray(response.data)) {
            return EMPTY_PAGE;
        }

        return {
            rows: response.data.map(toScheduleRowViewModel),
            total: response.total,
            nextStartKey: response.nextStartKey,
        };
    },

    /**
     * Loops the next-cursor until the backend reports there's nothing left, collecting rows in
     * memory as it goes. Nothing is returned to the caller until the drain completes, so an
     * abort partway through simply throws away that in-progress array - no partial state is
     * ever written or handed back.
     */
    async getAllSchedules(options?: IGetAllSchedulesOptions): Promise<IScheduleRowViewModel[]> {
        const rows: IScheduleRowViewModel[] = [];
        let startKey: string | null = null;
        let total: number | undefined;

        do {
            // Each page's cursor depends on the previous page's response, so the requests are
            // inherently sequential - there's nothing to parallelize here.
            // eslint-disable-next-line no-await-in-loop
            const response: IGetAllSchedulesResponse | undefined = await executeGetAllSchedulesRequest(
                {
                    pageSize: SCHEDULES_DRAIN_PAGE_SIZE,
                    status: options?.status,
                    type: options?.type,
                    startKey,
                    total,
                },
                { signal: options?.signal },
            );

            if (options?.signal?.aborted) {
                throw new DOMException("Aborted", "AbortError");
            }

            if (!response || !Array.isArray(response.data)) {
                break;
            }

            rows.push(...response.data.map(toScheduleRowViewModel));
            startKey = response.nextStartKey;
            total = response.total;
        } while (startKey);

        return rows;
    },
};

export default SchedulesRepository;
